import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient } from "@tanstack/react-query";
import { login } from "../api/auth";
import { getUnknownEmailTldLabel } from "../utils/emailTldWarning";
import "./login.css";

function resolveLoginError(err: unknown, t: TFunction): string {
    const ax = err as {
        response?: { data?: { errorCode?: string; message?: string } };
        message?: string;
    };
    const code = ax.response?.data?.errorCode;
    if (code === "invalid_credentials") {
        return t("login.error.invalidCredentials");
    }
    if (code === "account_not_approved") {
        return t("login.error.accountNotApproved");
    }
    if (code === "account_locked") {
        return t("login.error.accountLocked");
    }
    if (code === "invalid_totp") {
        return t("login.error.invalidTotp");
    }
    if (ax.response?.data?.message) {
        return ax.response.data.message;
    }
    if (ax.message) {
        return ax.message;
    }
    return t("login.error.generic");
}

export const Login = (): JSX.Element => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [userNameOrEmail, setUserNameOrEmail] = useState("");
    const [password, setPassword] = useState("");
    const [totpCode, setTotpCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totpRequired, setTotpRequired] = useState(false);

    const unknownEmailTld = useMemo(() => getUnknownEmailTldLabel(userNameOrEmail), [userNameOrEmail]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const response = await login(userNameOrEmail, password, totpCode || undefined);

            if (response.totpRequired && !totpCode) {
                setTotpRequired(true);
                setIsLoading(false);
                return;
            }

            // Invalidate auth status query to re-check
            await queryClient.invalidateQueries({ queryKey: ["auth", "status"] });
            // Invalidate other queries to retry with auth
            queryClient.invalidateQueries();
        } catch (err: unknown) {
            setError(resolveLoginError(err, t));
            setTotpRequired(false);
            setTotpCode("");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>{t("app.title")}</h1>
                    <p className="login-subtitle">{t("login.subtitle")}</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    {error && (
                        <div className="login-error" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="login-field">
                        <label htmlFor="username">{t("login.username.label")}</label>
                        <input
                            id="username"
                            type="email"
                            value={userNameOrEmail}
                            onChange={(e) => setUserNameOrEmail(e.target.value)}
                            placeholder={t("login.username.placeholder")}
                            required
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            disabled={isLoading}
                        />
                        {unknownEmailTld && (
                            <p className="login-hint login-hint--warn" role="status">
                                {t("login.warning.unknownTld", { tld: unknownEmailTld })}
                            </p>
                        )}
                    </div>

                    <div className="login-field">
                        <label htmlFor="password">{t("login.password.label")}</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t("login.password.placeholder")}
                            required
                            autoComplete="current-password"
                            disabled={isLoading}
                        />
                    </div>

                    {totpRequired && (
                        <div className="login-field">
                            <label htmlFor="totp">{t("login.totp.label")}</label>
                            <input
                                id="totp"
                                type="text"
                                value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value)}
                                placeholder={t("login.totp.placeholder")}
                                required
                                autoComplete="one-time-code"
                                disabled={isLoading}
                                maxLength={6}
                                pattern="[0-9]{6}"
                            />
                            <p className="login-hint">{t("login.totp.hint")}</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="login-button"
                        disabled={isLoading}
                    >
                        {isLoading ? t("login.button.loading") : t("login.button.submit")}
                    </button>
                </form>

                <div style={{ textAlign: "center", marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <Link
                        to="/password-reset"
                        style={{
                            color: "#93c5fd",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                        }}
                    >
                        Glömt lösenord?
                    </Link>
                    <Link
                        to="/register"
                        style={{
                            color: "#93c5fd",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                        }}
                    >
                        Registrera ny användare
                    </Link>
                </div>
            </div>
        </div>
    );
};